import Price from "pages2/Checkout/Price";
import { Link } from "react-router-dom";
import { getActiveCurrencyCodeLabel } from "utils";
import { BookFormat } from "utils/server-constants/bookFormats";
import { FiLock, FiTruck, FiArrowRight, FiShield, FiBook, FiPackage } from "react-icons/fi";
import { BsHeadset } from "react-icons/bs";

const calculateTotals = (cartItems = []) => {
	return cartItems.reduce(
		(acc, item) => {
			const quantity = Number(item?.quantity || 0);
			const isPhysical = item?.format === BookFormat.Physical;
			const usdUnitPrice = isPhysical
				? Number(item?.book?.physical_usd_price || 0)
				: Number(item?.book?.price || 0);
			const zwlUnitPrice = isPhysical
				? Number(item?.book?.physical_zwl_price || 0)
				: Number(item?.book?.zwl_price || 0);

			acc.usd += usdUnitPrice * quantity;
			acc.zwl += zwlUnitPrice * quantity;
			return acc;
		},
		{ usd: 0, zwl: 0 }
	);
};

const OrderSummary = ({
	cart,
	currency,
	isCompact = false,
	showCheckoutButton = true,
	showTrustBadges = true,
}) => {
	const currencyLabel = getActiveCurrencyCodeLabel(currency);
	const cartItems = cart?.cart_items || [];
	const itemCount = cartItems.length;
	const totalQuantity = cartItems.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
	const hasPhysicalItems = cartItems.some((item) => item.format === BookFormat.Physical);
	const hasDigitalItems = cartItems.some((item) => item.format !== BookFormat.Physical);
	const totals = calculateTotals(cartItems);

	return (
		<div
			className={`rounded-2xl border border-gray-200 bg-white overflow-hidden ${
				isCompact ? "shadow-sm" : "shadow-md"
			}`}
		>
			<div className="bg-gray-50/80 px-4 sm:px-5 py-4 border-b border-gray-200">
				<div className="flex items-center justify-between">
					<h3 className="text-lg sm:text-xl font-semibold text-gray-900">Order Summary</h3>
					<span className="inline-flex items-center justify-center bg-akgreener text-white text-xs font-bold px-2.5 py-1 rounded-full min-w-[24px]">
						{totalQuantity}
					</span>
				</div>
				<p className="text-sm text-gray-500 mt-1">
					{itemCount} {itemCount === 1 ? "item" : "items"} • {currencyLabel}
				</p>
			</div>

			<div className="p-4 sm:p-5">
				{hasPhysicalItems && (
					<div className="flex items-center gap-2 bg-blue-50 text-blue-700 px-3 py-2 rounded-lg text-xs mb-3">
						<FiTruck className="w-4 h-4 flex-shrink-0" />
						<span>Delivery fee is calculated at checkout for physical books</span>
					</div>
				)}
				{hasDigitalItems && (
					<div className="flex items-center gap-2 bg-akgreen/10 text-akgreenest px-3 py-2 rounded-lg text-xs mb-4">
						<FiBook className="w-4 h-4 flex-shrink-0" />
						<span>Digital books unlock instantly after purchase</span>
					</div>
				)}

				<div className="space-y-3 mb-5">
					{cartItems.map((cartItem) => {
						const isPhysical = cartItem?.format === BookFormat.Physical;
						const ItemIcon = isPhysical ? FiPackage : FiBook;
						return (
							<div className="flex items-start justify-between gap-3 text-sm" key={cartItem.id}>
								<div className="flex items-start gap-2 flex-1 min-w-0">
									<ItemIcon
										className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
											isPhysical ? "text-blue-500" : "text-akgreener"
										}`}
									/>
									<div className="min-w-0">
										<p className="font-medium text-gray-900 truncate">{cartItem.book_title}</p>
										<p className="text-gray-500 text-xs">
											Qty: {cartItem.quantity} • {cartItem.format}
										</p>
									</div>
								</div>
								<div className="text-right whitespace-nowrap font-medium text-gray-900">
									<Price
										sellable={true}
										usdPrice={
											(isPhysical
												? Number(cartItem?.book?.physical_usd_price || 0)
												: Number(cartItem?.book?.price || 0)) * Number(cartItem?.quantity || 0)
										}
										zwlPrice={
											(isPhysical
												? Number(cartItem?.book?.physical_zwl_price || 0)
												: Number(cartItem?.book?.zwl_price || 0)) * Number(cartItem?.quantity || 0)
										}
										isFree={false}
									/>
								</div>
							</div>
						);
					})}
				</div>

				<div className="border-t border-gray-200 pt-4 space-y-2.5">
					<div className="flex items-center justify-between text-sm text-gray-700">
						<span>Subtotal</span>
						<span className="font-medium">
							{currencyLabel}{" "}
							<Price sellable={true} usdPrice={totals.usd} zwlPrice={totals.zwl} isFree={false} />
						</span>
					</div>
					{hasPhysicalItems && (
						<div className="flex items-center justify-between text-xs text-gray-500">
							<span>Delivery</span>
							<span>Calculated at checkout</span>
						</div>
					)}
					<div className="flex items-center justify-between pt-2 border-t border-gray-200">
						<span className="text-base font-semibold text-gray-900">Total</span>
						<span className="text-lg sm:text-xl font-bold text-gray-900">
							{currencyLabel}{" "}
							<Price sellable={true} usdPrice={totals.usd} zwlPrice={totals.zwl} isFree={false} />
						</span>
					</div>
				</div>

				{showCheckoutButton && (
					<Link
						to="/checkout"
						className="w-full mt-5 inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl
							bg-gradient-to-r from-akgreen to-akgreener text-white font-semibold
							hover:from-akgreener hover:to-akgreenest transition-all duration-300
							focus:outline-none focus:ring-2 focus:ring-akgreener focus:ring-offset-2
							shadow-sm hover:shadow-md"
					>
						<FiLock className="w-4 h-4" />
						Proceed to Checkout
						<FiArrowRight className="w-4 h-4" />
					</Link>
				)}

				{showTrustBadges && !isCompact && (
					<div className="mt-5 pt-4 border-t border-gray-100">
						<div className="grid grid-cols-2 gap-3">
							<div className="flex items-center gap-2 text-xs text-gray-600">
								<div className="w-7 h-7 rounded-full bg-green-50 flex items-center justify-center">
									<FiShield className="w-3.5 h-3.5 text-green-600" />
								</div>
								<span className="font-medium">Secure Payment</span>
							</div>
							<div className="flex items-center gap-2 text-xs text-gray-600">
								<div className="w-7 h-7 rounded-full bg-blue-50 flex items-center justify-center">
									<BsHeadset className="w-3.5 h-3.5 text-blue-600" />
								</div>
								<span className="font-medium">24/7 Support</span>
							</div>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default OrderSummary;
